// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaResultDiplomaFields from '../../fields/RegattaResultDiplomaFields';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaResultDiploma'>>;
  loading: boolean;
};

const pageId = adminPages.regattaResultDiplomasPage;

class RegattaResultDiplomasPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'RegattaResultDiploma') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegattaResultDiploma({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    if (RegattaId !== null) {
      const newItem: $ModelUpsertParams<'RegattaResultDiploma'> = {
        Content: '',
        Name: '',
        RegattaId,
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            iconClass={''}
            rightButtons={(
              <FormDialog
                fields={(
                  <RegattaResultDiplomaFields item={newItem} />
                )}
                id={'create-regatta-result-dilpoma-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'RegattaResultDiploma'>>({
                      ...newItem,
                      ...values,
                    });

                    await Services.Api.upsertRegattaResultDiploma(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Regatta result diploma')}
              >
                <Button
                  color={'info'}
                  id={'create-regatta-result-dilpoma-button'}
                  size={'sm'}
                  title={t('Add new regatta result diploma template')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Regatta result diplomas')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'RegattaResultDiploma'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaResultDiplomaFields item={dataItem} />
          )}
          id={`update-regatta-result-diploma-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaResultDiploma'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaResultDiploma(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta result diploma')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-result-diploma-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-result-diploma-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaResultDiploma(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta result diploma')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-result-diploma-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaResultDiploma'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaResultDiplomasPageView;
